/**
 * Browser-side context for the "send this to the dev team" flow (send_dev_request).
 *
 * The server sees only the question, the message list and a url path. Everything that
 * actually explains a bad result — the report spec on screen, the active filter criteria,
 * the selected market and date range — lives in the Redux store and would otherwise be
 * lost.
 *
 * This snapshot is built ONLY when a dev request is actually filed, not on every chat
 * message: the server answers send_dev_request with a `dev_request:<ref>` update_state
 * flag, and updateStateRouter posts the snapshot back as a second leg. Filing a report is
 * rare; chatting is not, so the cost belongs on the report rather than on every turn.
 *
 * Two rules for anything added below:
 *   1. Whitelist named fields. Never spread a store slice — the store grows, and a spread
 *      would silently start shipping whatever gets added to it.
 *   2. No result rows. `aiReport.rows` holds provider/claims records; the spec and columns
 *      are what a developer needs, the rows are PHI-adjacent and are excluded server-side
 *      as well (see devRequestService).
 */

// Failures are recorded here as they happen, because by the time the user types "send this
// to the dev team" the failing turn is already over and its error has been swallowed into a
// generic chat message. Without this buffer the field that matters most on an error report
// would always be empty. Module-scoped: one buffer per loaded app, cleared on reload.
const MAX_ERRORS = 5;
let recentErrors = [];

/**
 * Record a frontend failure for inclusion in the next dev request.
 * @param {Error|object} error   the thrown error (axios error, TypeError, ...)
 * @param {string} source        where it happened, e.g. 'ActionProvider.post'
 */
export function recordChatError(error, source) {
  try {
    const entry = {
      at: new Date().toISOString(),
      source: source || 'unknown',
      message: String(error?.message ?? error ?? 'unknown error').slice(0, 500),
      // Axios shape — the HTTP status and endpoint are usually the whole story.
      status: error?.response?.status ?? null,
      url: error?.config?.url ?? null,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    };
    recentErrors = [...recentErrors, entry].slice(-MAX_ERRORS);
  } catch (e) {
    // Never let diagnostics break the chat.
    console.warn('[devrequest] failed to record error:', e?.message);
  }
}

export function getRecentChatErrors() {
  return recentErrors;
}

export function clearChatErrors() {
  recentErrors = [];
}

/**
 * The open AI report's identity, sent with EVERY Key request (text + voice) as `open_report`.
 * The backend keeps the open report in per-Lambda-worker memory, which the next turn often
 * misses (or finds stale); this lets it rebuild that cache for "those destinations" / "add a
 * column" / "save to a label" follow-ups. Whitelisted fields only — spec, title, columns.
 * NEVER rows (see rule 2 above).
 * @param {object} report  the `state.app.aiReport` slice
 */
export function buildOpenReportContext(report) {
  try {
    if (!report || !report.open || !report.spec) return null;
    return {
      title: report.title ?? null,
      spec: report.spec,
      columns: report.columns ?? null,
    };
  } catch (e) {
    return null;
  }
}

/**
 * Build the snapshot posted as `client_state` in leg 2 of a dev request.
 * @param {object} app  the `state.app` slice
 */
export function buildClientState(app) {
  try {
    const a = app || {};
    const report = a.aiReport || null;
    const filter = a.aiLabelFilter || null;

    return {
      selectedClient: a.selectedClient ?? null,
      dateRange: a.dateRange ?? null,
      market: a.market ?? null,
      serviceline: a.serviceline ?? null,
      patientType: a.patientType ?? null,
      providerType: a.providerType ?? null,
      // NOTE: state.app.report is deliberately not included — the APP_SET_REPORT action
      // has no dispatchers or readers left, so it is always undefined.
      query: a.query ?? null,

      // rows deliberately omitted — see the header note.
      aiReport: report ? {
        open: !!report.open,
        title: report.title ?? null,
        reportType: report.reportType ?? null,
        claimsType: report.claimsType ?? null,
        visualization: report.visualization ?? null,
        spec: report.spec ?? null,
        columns: report.columns ?? null,
        count: report.count ?? null,
      } : null,

      aiLabelFilter: filter ? {
        open: !!filter.open,
        mode: filter.mode ?? null,
        namespaceName: filter.namespaceName ?? null,
        tagName: filter.tagName ?? null,
        namespaceId: filter.namespaceId ?? null,
        tagId: filter.tagId ?? null,
        initialPrompt: filter.initialPrompt ?? null,
        criteria: filter.criteria ?? null,
        currentQuery: filter.currentQuery ?? null,
        savedResult: filter.savedResult ?? null,
      } : null,

      recent_errors: getRecentChatErrors(),

      browser: typeof window !== 'undefined' ? {
        user_agent: window.navigator?.userAgent ?? null,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        path: window.location?.pathname ?? null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
      } : null,
    };
  } catch (e) {
    // A malformed store must not stop the user from chatting.
    console.warn('[devrequest] failed to build client state:', e?.message);
    return { build_error: String(e?.message ?? e) };
  }
}
